import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { ROLE_LOAN_OFFICER, formatAdminRole, normalizeRole } from "../accessControl";
import { callApi, getInlineLoader } from "../Helpers";
import {
  MdAdminPanelSettings,
  MdBadge,
  MdKeyboardArrowDown,
  MdLock,
  MdPhoneIphone,
  MdSecurity,
  MdEmail
} from "react-icons/md";

export default function View({ id }) {
  const appContext = useContext(AppContext);

  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fullname, setFullname] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState(""); // Added email state
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("admin");
  const [data, setData] = useState(null);

  const roleOptions = [
    { value: "admin", label: "Administrator", helper: "Full platform access and overall operations control." },
    { value: "cashier", label: "Cashier", helper: "Focused on payment intake, posting, and transaction handling." },
    { value: ROLE_LOAN_OFFICER, label: "Loan Officer", helper: "Focused on assigned loan follow-up, account monitoring, and debt recovery coordination." },
  ];

  async function init() {
    setReady(false);
    if (id) {
      await getData();
    }
    setReady(true);
  }

  async function getData() {
    try {
      const response = await callApi("get-admin-data", { id });
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data");
      }
    } catch (err) {
      appContext.tellError("System error");
    }
  }

  const validate = () => {
    if (!fullname.trim()) return appContext.tellError("Invalid Admin Full Name");
    if (!phone.trim()) return appContext.tellError("Invalid Admin Phone Number");
    if (!email.trim() || !email.includes('@')) return appContext.tellError("Invalid Admin Email Address"); // Email validation
    if (!role.trim()) return appContext.tellError("Admin role is required");
    if (!id && !password.trim()) return appContext.tellError("Password is required for new admins");
    return true;
  };

  async function handleAction() {
    if (validate() !== true) return;

    setLoading(true);
    const endpoint = id ? "update-admin" : "create-admin";
    const payload = {
      id,
      fullname: fullname.trim(),
      phone: phone.trim(),
      email: email.trim(), // Added to payload
      password,
      role,
    };

    try {
      const response = await callApi(endpoint, payload);
      if (response.status === 1) {
        appContext.tellMessage("Successful");
        appContext.navBack();
        appContext.getAllAdmins();
      } else {
        appContext.tellError(response.msg);
      }
    } catch (err) {
      appContext.tellError("Request failed");
    }
    setLoading(false);
  }

  useEffect(() => {
    init();
  }, [id]);

  useEffect(() => {
    if (data) {
      setFullname(data.fullname || "");
      setPhone(data.phone || "");
      setEmail(data.emails || data.email || ""); // Handle mapping from DB (emails)
      setRole(normalizeRole(data.role) || "admin");
    }
  }, [data]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => appContext.setShowOverlayLoader(false);
  }, [loading]);

  if (!ready) {
    return <div className="container py-5">{getInlineLoader()}</div>;
  }

  return (
    <div className="container">
      <div className="app-form-page">
        <div className="app-form-shell">
          <div className="app-form-header">
            <div>
              <h6 className="app-form-title">{id ? "Update Administrator" : "Register Administrator"}</h6>
              <p className="app-form-subtitle">
                Create internal user accounts with the right operational role for platform oversight, payment handling, or collections follow-up.
              </p>
            </div>

            <div className="app-form-badge">
              <MdAdminPanelSettings size={18} />
              {id ? "Editing access profile" : "New team account"}
            </div>
          </div>

          <div className="app-form-section">
            <div className="app-form-summary-grid">
              <div className="app-form-summary-card">
                <span>Account type</span>
                <strong>{roleOptions.find((item) => item.value === role)?.label || formatAdminRole(role)}</strong>
              </div>

              <div className="app-form-summary-card">
                <span>Password</span>
                <strong>{id ? "Optional on update" : "Required on create"}</strong>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mt-3 mb-3">
                <div className="app-form-input-wrap">
                  <label className="form-label">Full Name</label>
                  <input
                    className="form-control"
                    value={fullname}
                    placeholder="Enter administrator name"
                    onChange={(e) => setFullname(e.target.value)}
                  />
                  <div className="app-form-helper">
                    <MdBadge size={14} /> Use the official full name that should appear across admin records.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mt-3 mb-3">
                <div className="app-form-input-wrap">
                  <label className="form-label">Phone Number</label>
                  <input
                    type="number"
                    className="form-control"
                    value={phone}
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="Enter mobile number"
                    onChange={(e) => setPhone(e.target.value)}
                  />
                  <div className="app-form-helper">
                    <MdPhoneIphone size={14} /> This number is used for account identification and operational contact.
                  </div>
                </div>
              </div>

              {/* Added Email Field */}
              <div className="col-md-6 mt-3 mb-3">
                <div className="app-form-input-wrap">
                  <label className="form-label">Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    value={email}
                    placeholder="Enter official email"
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <div className="app-form-helper">
                    <MdEmail size={14} /> Receives system alerts and payment notifications.
                  </div>
                </div>
              </div>

              <div className="col-md-6 mt-3 mb-3">
                <div className="app-form-input-wrap">
                  <label className="form-label">Role</label>
                  <div className="position-relative">
                    <select
                      className="form-control pe-5"
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                    >
                      {roleOptions.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <MdKeyboardArrowDown
                      size={22}
                      style={{
                        position: "absolute",
                        right: "14px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none",
                        color: "rgba(16, 35, 61, 0.6)",
                      }}
                    />
                  </div>
                  <div className="app-form-helper">
                    <MdSecurity size={14} /> {roleOptions.find((item) => item.value === role)?.helper}
                  </div>
                </div>
              </div>

              <div className="col-md-6 mt-3 mb-3">
                <div className="app-form-input-wrap">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={password}
                    placeholder={id ? "Leave blank to keep current password" : "Create secure password"}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <div className="app-form-helper">
                    <MdLock size={14} /> {id ? "Leave this field empty if you do not want to change the current password." : "Set a secure password for the new account."}
                  </div>
                </div>
              </div>

              {!id ? (
                <div className="col-12 mt-2 mb-3">
                  <div className="app-form-summary-card">
                    <span>Access note</span>
                    <strong>Permissions are assigned by role after registration.</strong>
                  </div>
                </div>
              ) : (
                ""
              )}

              <div className="col-12 mt-3 mb-3 text-end app-form-submit-row">
                <button
                  className="btn btn-padded btn-rounded btn-primary btn-block app-form-submit-btn"
                  onClick={handleAction}
                >
                  {id ? "SAVE ADMIN UPDATES" : "REGISTER ADMINISTRATOR"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}